// ProductPicker — searchable combobox for selecting a product.
// Caller supplies a pre-filtered `products` list (the component does not own filtering rules).
// Items in `disabledIds` render greyed out with a small badge and cannot be picked.
//
// Props:
//   products      [{ id, sku, name }]   list to choose from
//   value         string                currently selected product id ('' for none)
//   onChange      (id) => void          fires with new id (or '' when cleared)
//   disabledIds   string[]              ids to show greyed/disabled
//   disabledLabel string                badge text for disabled rows (default: 'added')
//   placeholder   string
//   style         object                forwarded to the wrapper div

import { useEffect, useMemo, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { searchFilter } from '../lib/search'

export default function ProductPicker({
  products = [],
  value = '',
  onChange,
  disabledIds = [],
  disabledLabel = 'added',
  placeholder = 'Search products...',
  style = {},
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const wrapRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const selected = useMemo(
    () => products.find(p => p.id === value) || null,
    [products, value]
  )

  const disabledSet = useMemo(() => new Set(disabledIds), [disabledIds])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return products
    // A complete SKU or ASIN shows only that product (lib/search)
    return products.filter(searchFilter(products, q, (p, qq) =>
      (p.sku || '').toLowerCase().includes(qq) ||
      (p.name || '').toLowerCase().includes(qq) ||
      (p.asin || '').toLowerCase().includes(qq)
    ))
  }, [products, query])

  // Reset query when value is cleared externally (e.g., parent resets after adding)
  useEffect(() => { if (!value) setQuery('') }, [value])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function onDocClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Keep highlight in range
  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0)
  }, [filtered.length, highlight])

  // Scroll highlighted row into view
  useEffect(() => {
    if (!open || !listRef.current) return
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`)
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' })
  }, [highlight, open])

  function pick(p) {
    if (!p || disabledSet.has(p.id)) return
    onChange?.(p.id)
    setQuery('')
    setOpen(false)
  }

  function moveHighlight(dir) {
    if (filtered.length === 0) return
    let next = highlight
    for (let i = 0; i < filtered.length; i++) {
      next = (next + dir + filtered.length) % filtered.length
      if (!disabledSet.has(filtered[next].id)) break
    }
    setHighlight(next)
  }

  function onKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true)
      return
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1) }
    else if (e.key === 'Enter') { e.preventDefault(); pick(filtered[highlight]) }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() }
  }

  const displayValue = open
    ? query
    : (selected ? `${selected.sku} — ${selected.name}` : query)

  return (
    <div ref={wrapRef} style={{ position: 'relative', ...style }}>
      <div style={{ position: 'relative' }}>
        <Search
          size={14}
          style={{
            position: 'absolute', left: '10px', top: '50%',
            transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none'
          }}
        />
        <input
          ref={inputRef}
          className="input"
          style={{ paddingLeft: '32px', paddingRight: value ? '32px' : '12px' }}
          placeholder={placeholder}
          value={displayValue}
          onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(0) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {value && (
          <button
            type="button"
            onClick={() => { onChange?.(''); setQuery(''); inputRef.current?.focus() }}
            style={{
              position: 'absolute', right: '8px', top: '50%',
              transform: 'translateY(-50%)', background: 'transparent', border: 'none',
              color: 'var(--text-3)', cursor: 'pointer', padding: '2px',
              display: 'flex', alignItems: 'center'
            }}
            aria-label="Clear"
          >
            <X size={14} />
          </button>
        )}
      </div>
      {open && (
        <div
          ref={listRef}
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            background: 'var(--bg-3)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            maxHeight: '280px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 6px 16px rgba(0,0,0,0.35)',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: '12px', color: 'var(--text-3)' }}>
              No matches
            </div>
          ) : filtered.map((p, i) => {
            const isDisabled = disabledSet.has(p.id)
            const isHighlight = i === highlight
            return (
              <div
                key={p.id}
                data-idx={i}
                onMouseEnter={() => !isDisabled && setHighlight(i)}
                onMouseDown={e => { e.preventDefault(); pick(p) }}
                style={{
                  padding: '8px 12px',
                  fontSize: '13px',
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  background: isHighlight && !isDisabled ? 'var(--bg-4)' : 'transparent',
                  opacity: isDisabled ? 0.45 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: '11px', color: 'var(--text-3)' }}>{p.sku}</span>
                  <span style={{ color: 'var(--text-2)', marginLeft: '8px' }}>{p.name}</span>
                </span>
                {isDisabled && (
                  <span className="badge badge-neutral" style={{ fontSize: '10px', flexShrink: 0 }}>
                    {disabledLabel}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
